import { type Hex, hexKey } from '../core/hex';

/**
 * Vision radius — ADR-0014 §1 holds the slice-1 value at centre + ring-1 +
 * ring-2 = 19 hexes. With the tier collapse to {vision, memory, frontier}
 * the full disk becomes a single "active vision" set; ring-2 is no longer a
 * distinct tier and renders bright like ring-1.
 */
export const DEFAULT_VISION_RADIUS = 2;

export interface PositionSource {
  readonly position: Hex;
}

/**
 * Field-of-view (ADR-0014 §1). `vision` is the centre + every hex within
 * `radius` steps — the renderer paints all of them as "active" mode when
 * their tile is globally discovered, and as "frontier" `?` cardbacks when
 * their tile is undiscovered.
 *
 * Memory mode (discovered out of vision) is composed app-side from the
 * global discovered set minus this disk and pushed to the renderer alongside.
 */
export interface FoV {
  readonly vision: Set<string>;
}

/**
 * Vision = the player's current FoV, recomputed each call from the live
 * position source. Radius governs the size of the vision disk (default 2 =
 * centre + ring-1 + ring-2 = 19 hexes per ADR-0014 §1).
 */
export class Vision {
  readonly radius: number;
  private readonly source: PositionSource;

  constructor(source: PositionSource, radius: number = DEFAULT_VISION_RADIUS) {
    if (!Number.isInteger(radius) || radius < 0) {
      throw new Error(`Vision radius must be a non-negative integer, got ${radius}`);
    }
    this.source = source;
    this.radius = radius;
  }

  /** The vision disk as a flat key set. */
  getVisibleHexes(): Set<string> {
    return computeVisibleHexes(this.source.position, this.radius);
  }

  /** Same as `getVisibleHexes`, returned as the FoV shape the renderer takes. */
  getFoV(): FoV {
    return computeFoV(this.source.position, this.radius);
  }
}

/**
 * Every hex within `radius` of `centre`, keyed by `hexKey`. Pure helper for
 * tests and headless callers that don't need a `Vision` instance.
 */
export function computeVisibleHexes(centre: Hex, radius: number): Set<string> {
  const out = new Set<string>();
  for (let dq = -radius; dq <= radius; dq++) {
    const drMin = Math.max(-radius, -dq - radius);
    const drMax = Math.min(radius, -dq + radius);
    for (let dr = drMin; dr <= drMax; dr++) {
      out.add(hexKey({ q: centre.q + dq, r: centre.r + dr }));
    }
  }
  return out;
}

/**
 * Compute the ring at exactly `distance` steps from `centre`. Returns the empty
 * set when `distance` is 0 (the centre itself is not a ring) — distinct from
 * the disk computed by `computeVisibleHexes`.
 */
export function computeRing(centre: Hex, distance: number): Set<string> {
  const out = new Set<string>();
  if (distance <= 0) return out;
  for (let dq = -distance; dq <= distance; dq++) {
    const drMin = Math.max(-distance, -dq - distance);
    const drMax = Math.min(distance, -dq + distance);
    for (let dr = drMin; dr <= drMax; dr++) {
      const absS = Math.abs(-dq - dr);
      if (Math.max(Math.abs(dq), Math.abs(dr), absS) === distance) {
        out.add(hexKey({ q: centre.q + dq, r: centre.r + dr }));
      }
    }
  }
  return out;
}

/**
 * FoV around `centre` — a single disk at the given radius. Memory composition
 * is the App's responsibility (it needs the global discovered set, which
 * Vision doesn't see).
 */
export function computeFoV(centre: Hex, radius: number): FoV {
  return { vision: computeVisibleHexes(centre, radius) };
}
